import { useState } from "react";
import { History } from "lucide-react";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	SearchableSelect,
	type SearchableSelectOption,
} from "@/components/ui/searchable-select";
import { formatTime } from "../types";
import { listSprints, type Sprint } from "@/services/sprint.service";
import {
	getTaskEstimateHistory,
	updateTaskSprintEstimate,
	type EstimateHistoryEntry,
} from "@/services/task-estimate-history.service";
import { reportError } from "./utils";

export function EstimateHistoryPopover({
	projectId,
	taskId,
	canEdit,
}: {
	projectId: string;
	taskId: string;
	canEdit: boolean;
}) {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [rows, setRows] = useState<EstimateHistoryEntry[]>([]);
	const [sprints, setSprints] = useState<Sprint[]>([]);
	const [savingId, setSavingId] = useState<string | null>(null);
	const [editingId, setEditingId] = useState<string | null>(null);

	async function load() {
		setLoading(true);
		setError(false);
		try {
			const [history, sprintList] = await Promise.all([
				getTaskEstimateHistory(projectId, taskId),
				canEdit ? listSprints() : Promise.resolve([]),
			]);
			setRows(history);
			setSprints(sprintList);
		} catch {
			setError(true);
		} finally {
			setLoading(false);
		}
	}

	function onOpenChange(next: boolean) {
		setOpen(next);
		if (next) load();
	}

	async function changeSprint(row: EstimateHistoryEntry, sprintId: string) {
		setEditingId(null);
		if (!sprintId || sprintId === row.sprint?.id) return;
		setSavingId(row.id);
		try {
			const updated = await updateTaskSprintEstimate(
				projectId,
				taskId,
				row.id,
				sprintId,
			);
			setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
		} catch (e) {
			reportError(e);
		} finally {
			setSavingId(null);
		}
	}

	const sprintOptions: SearchableSelectOption[] = sprints.map((s) => ({
		value: s.id,
		label: s.name,
	}));

	return (
		<Popover open={open} onOpenChange={onOpenChange}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="flex items-center gap-1 text-[10px]! font-medium text-muted hover:text-primary transition-colors"
				>
					<History className="h-3 w-3" />
					History
				</button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-72 p-0">
				<div className="px-3 py-2 border-b border-border">
					<p className="text-[10px] font-semibold uppercase tracking-wider text-muted">
						Estimate History
					</p>
				</div>
				<div className="max-h-64 overflow-y-auto p-1">
					{loading && (
						<p className="px-2 py-3 text-xs text-muted">Loading…</p>
					)}
					{!loading && error && (
						<p className="px-2 py-3 text-xs text-destructive">
							Failed to load history.
						</p>
					)}
					{!loading && !error && rows.length === 0 && (
						<p className="px-2 py-3 text-xs text-muted">
							No estimates recorded yet.
						</p>
					)}
					{!loading &&
						!error &&
						rows.map((r) => (
							<div
								key={r.id}
								className="flex items-center justify-between gap-2 rounded px-2 py-1.5 hover:bg-muted-subtle/60"
							>
								{canEdit && editingId === r.id ? (
									<div className="flex-1 min-w-0">
										<SearchableSelect
											value={r.sprint?.id ?? ""}
											options={sprintOptions}
											disabled={savingId === r.id}
											size="sm"
											autoFocus
											defaultMenuIsOpen
											onValueChange={(v) =>
												changeSprint(r, v)
											}
											onMenuClose={() =>
												setEditingId(null)
											}
											placeholder="Select sprint"
										/>
									</div>
								) : (
									<button
										type="button"
										disabled={!canEdit || savingId === r.id}
										onClick={() => setEditingId(r.id)}
										className={
											canEdit
												? "flex-1 min-w-0 text-left text-xs! text-foreground truncate rounded px-1 py-0.5 -mx-1 hover:bg-muted-subtle/60 transition-colors cursor-text"
												: "flex-1 min-w-0 text-left text-xs text-foreground truncate"
										}
									>
										{r.sprint?.name ?? "—"}
									</button>
								)}
								<span className="text-xs font-medium text-secondary whitespace-nowrap">
									{formatTime(r.estimated_time)}
								</span>
							</div>
						))}
				</div>
			</PopoverContent>
		</Popover>
	);
}
